import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { Heading, Text } from 'tio-ui/components/typography';
import { PhFire, PhTrophy, PhMedal, PhQuestion } from 'ember-phosphor-icons';
import type { CareerCapitalScore } from 'tio-employee/services/career-pathways-api';

/**
 * MomentumPanel - Displays gamification elements: score, streak, badges
 *
 * This component shows the user's "momentum" including:
 * - Career Capital Score with percentile ranking
 * - Current learning streak
 * - Recent achievement badge
 * - Help/support link
 *
 * @example
 * ```gts
 * <MomentumPanel
 *   @score={{this.score}}
 *   @companyName="Advocate Health"
 *   @streakDays={{14}}
 * />
 * ```
 */
interface MomentumPanelSignature {
  Args: {
    /** Career Capital Score data */
    score: CareerCapitalScore;
    /** Company name for percentile context */
    companyName: string;
    /** Current learning streak in days */
    streakDays?: number;
    /** Route for help/support link (defaults to contact-us) */
    helpRoute?: string;
  };
  Element: HTMLDivElement;
}

export default class MomentumPanel extends Component<MomentumPanelSignature> {
  get streakDays() {
    return this.args.streakDays ?? 14;
  }

  get helpRoute() {
    return this.args.helpRoute ?? 'authenticated.contact-us';
  }

  get firstBadge() {
    return this.args.score.badges[0];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 rounded-xl p-6 border border-violet-100 dark:border-violet-800"
      role="complementary"
      aria-label="Your momentum and achievements"
      ...attributes
    >
      <div class="flex items-center gap-2 mb-4">
        <PhFire @size={{24}} class="text-orange-500 dark:text-orange-400" aria-hidden="true" />
        <Heading @level={{2}} class="text-lg font-semibold text-gray-900 dark:text-white">Your
          Momentum</Heading>
      </div>

      <div class="space-y-3">
        {{! Career Score }}
        <div class="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3">
          <div
            class="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-lg text-violet-600 dark:text-violet-400"
          >
            <PhTrophy @size={{20}} aria-hidden="true" />
          </div>
          <div class="flex-1">
            <div class="font-semibold text-gray-900 dark:text-white">
              {{@score.totalScore}}
              Career Score
            </div>
            <Text @intent="muted" class="text-xs dark:text-gray-400">
              Top
              {{@score.percentile}}% at
              {{@companyName}}
            </Text>
          </div>
        </div>

        {{! Streak }}
        <div class="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3">
          <div
            class="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-lg text-orange-500 dark:text-orange-400"
          >
            <PhFire @size={{20}} aria-hidden="true" />
          </div>
          <div class="flex-1">
            <div class="font-semibold text-gray-900 dark:text-white">
              {{this.streakDays}}
              day streak!
            </div>
            <Text @intent="muted" class="text-xs dark:text-gray-400">Keep it going</Text>
          </div>
        </div>

        {{! Recent Badge }}
        {{#if this.firstBadge}}
          <div class="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3">
            <div
              class="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg text-amber-500 dark:text-amber-400"
            >
              <PhMedal @size={{20}} aria-hidden="true" />
            </div>
            <div class="flex-1">
              <div
                class="font-semibold text-gray-900 dark:text-white"
              >{{this.firstBadge.name}}</div>
              <Text @intent="muted" class="text-xs dark:text-gray-400">Recent achievement</Text>
            </div>
          </div>
        {{/if}}
      </div>

      {{! Help Link }}
      <div class="mt-4 pt-4 border-t border-violet-200 dark:border-violet-700">
        <LinkTo
          @route={{this.helpRoute}}
          class="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
        >
          <PhQuestion @size={{18}} aria-hidden="true" />
          <span>Questions? We're here to help</span>
        </LinkTo>
      </div>
    </div>
  </template>
}
